import { useState, useRef, useMemo, useDeferredValue } from 'react';

const SEARCH_INDEX_URL = '/blog-search-index.json';
const MAX_RESULTS = 24;

/**
 * The search index is a flat, image-URL-only list of every published article,
 * written at build time by `onPostBuild` in gatsby-node.js. Keys are short to
 * keep the file small.
 */
const fromSearchIndex = ({ t, s, x, c, i }) => ({
    id: s,
    title: t,
    slug: s,
    summary: x,
    categories: c.map((name) => ({ name })),
    imageUrl: i
});

const matchesQuery = (query) => {
    const lcQuery = query.toLowerCase();
    // titles and summaries are editor-entered, so don't assume either is set
    const contains = (text) =>
        (text || '').toLowerCase().includes(lcQuery);

    return ({ title, summary }) => contains(title) || contains(summary);
};

/**
 * On a category listing, search stays inside that category - the pills are
 * hidden while searching, so results from everywhere would silently drop the
 * filter the URL says is applied.
 */
const inCategory = (categoryName) => {
    if (!categoryName) {
        return () => true;
    }

    return ({ categories }) =>
        (categories || []).some(({ name }) => name === categoryName);
};

/**
 * Free-text search across the blog catalogue.
 *
 * A listing page only holds its own page of articles, so the first keystroke
 * lazily fetches the build-time search index. Until it arrives - and if it
 * never does - search falls back to `fallbackArticles`, the articles already
 * rendered on the page.
 */
const useBlogSearch = (fallbackArticles, { categoryName } = {}) => {
    const [query, setQuery] = useState('');
    const [searchIndex, setSearchIndex] = useState(null);
    const indexRequested = useRef(false);

    const loadSearchIndex = () => {
        if (indexRequested.current) {
            return;
        }
        indexRequested.current = true;
        // `Promise.resolve` so a fetch that throws synchronously is caught too
        Promise.resolve()
            .then(() => fetch(SEARCH_INDEX_URL))
            .then((response) => response.json())
            .then((entries) =>
                setSearchIndex(entries.map(fromSearchIndex))
            )
            .catch(() => {
                // leave the index unset - search falls back to the
                // articles already rendered on this page
            });
    };

    const onSearchChange = (event) => {
        setQuery(event.target.value);
        loadSearchIndex();
    };

    /**
     * Filtering the whole catalogue is deprioritised so typing stays
     * responsive - the input renders the new value immediately and the results
     * catch up, rather than every keystroke blocking on ~800 articles and up
     * to 24 card re-renders.
     */
    const searchedQuery = useDeferredValue(query);

    const matches = useMemo(() => {
        if (!searchedQuery) {
            return [];
        }

        return (searchIndex || fallbackArticles)
            .filter(inCategory(categoryName))
            .filter(matchesQuery(searchedQuery));
    }, [searchedQuery, searchIndex, fallbackArticles, categoryName]);

    return {
        query,
        searchedQuery,
        onSearchChange,
        isSearching: !!searchedQuery,
        results: matches.slice(0, MAX_RESULTS),
        totalResults: matches.length,
        maxResults: MAX_RESULTS
    };
};

export default useBlogSearch;
